// AvailableFeatureDialog.js - Dialog for creating/editing available features
import {
  useState,
  useEffect,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Alert,
  CircularProgress
} from './SettingsCommon';

const AvailableFeatureDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    code: '',
    label: '',
    description: '',
    active: true,
    coming_soon: false,
    sort_order: 0
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (data) {
      setFormData({
        code:        data.code        || '',
        label:       data.label       || '',
        description: data.description || '',
        active:      data.active      ?? true,
        coming_soon: data.coming_soon ?? false,
        sort_order:  data.sort_order  ?? 0
      });
    } else {
      setFormData({ code: '', label: '', description: '', active: true, coming_soon: false, sort_order: 0 });
    }
    setError(null);
  }, [data, open]);

  const handleSubmit = async () => {
    if (!formData.code.trim() || !formData.label.trim()) {
      setError('Code and Label are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save feature');
    } finally {
      setSaving(false);
    }
  };

  const isEdit = !!data?.id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Feature' : 'Add New Feature'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Code (internal)"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
            fullWidth
            required
            placeholder="e.g., ALLOW_PATIO_DOORS"
            helperText="Uppercase, no spaces (use underscores)"
            disabled={isEdit}
          />
          <TextField
            label="Display Label"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            fullWidth
            required
            placeholder="e.g., Patio Doors"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
            placeholder="e.g., Enable Patio Door quoting and configuration"
          />
          <TextField
            label="Sort Order"
            type="number"
            value={formData.sort_order}
            onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            sx={{ width: 150 }}
          />
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
              }
              label="Active (global master switch)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.coming_soon}
                  onChange={(e) => setFormData({ ...formData, coming_soon: e.target.checked })}
                />
              }
              label="Coming Soon"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Save Changes' : 'Add Feature'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailableFeatureDialog;
